import { pathOr } from 'ramda';
import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useApplicationContext } from 'shared/map-search/containers/ApplicationContext';
import { useFeaturesContext } from 'shared/map-search/containers/Map/MapLayers/Features/context/context';
import { useMapContext } from 'shared/map-search/containers/Map/context/context';
import { selectParentRegionId } from 'shared/map-search/selectors/parentRegionId/selectParentRegionId';

import type { IFeaturesMap } from 'shared/map-search/types/map/map';
import type { TMapBounds } from 'shared/map-search/types/mapBounds/mapBounds';
import type { IApplicationState } from 'shared/map-search/types/redux/store/store';

const KEY: Record<number, string> = {
  0: 'pulsed',
  1: 'label',
  2: 'details',
};

export const MapTelemetry = () => {
  const { telemetry } = useApplicationContext();
  const { ymaps, map } = useMapContext();
  const { rbush } = useFeaturesContext();
  const { bounds, zoom, precision } = useSelector<IApplicationState, TMapBounds>(state => state.mapBounds);
  const features = useSelector<IApplicationState, IFeaturesMap>(state =>
    pathOr<IFeaturesMap>({}, [precision, 'features'], state.features),
  );
  const regionId = useSelector(selectParentRegionId);
  const initialSentRef = useRef(false);
  const telemetryKey = `map-promo-pin.regionId${regionId}.zoom${zoom}`;

  const getPoints = useCallback(
    (): [number, number, number] => {
      if (!bounds) {
        return [0, 0, 0];
      }

      const featuresInBbox = rbush.search({
        minX: bounds[0][0],
        minY: bounds[0][1],
        maxX: bounds[1][0],
        maxY: bounds[1][1],
      });

      return featuresInBbox.reduce(
        (acc, feature) => {
          acc[0] = acc[0] + (feature.feature.properties?.isPulsedGlow ? 1 : 0);
          acc[1] = acc[1] + (feature.feature.properties?.label ? 1 : 0);
          acc[2] = acc[2] + (feature.feature.properties?.details && zoom > 16 ? 1 : 0);

          return acc;
        },
        [0, 0, 0],
      );
    },
    // oxlint-disable-next-line react/exhaustive-deps
    [bounds, map, rbush, zoom],
  );

  const sendMapSearchTelemetry = useCallback(
    () => {
      const points = getPoints();

      points.forEach((count: number, index: number) => {
        if (count === 0) {
          return;
        }

        telemetry?.increment(`${telemetryKey}.${KEY[index]}`, count);
      });
    },
    // oxlint-disable-next-line react/exhaustive-deps
    [getPoints, telemetry, telemetryKey],
  );

  useEffect(() => {
    if (!ymaps || !map) {
      return;
    }

    map.events.add('boundschange', sendMapSearchTelemetry);

    return () => {
      map.events.remove('boundschange', sendMapSearchTelemetry);
    };
  }, [map, sendMapSearchTelemetry, ymaps]);

  useEffect(() => {
    if (initialSentRef.current || Object.keys(features).length === 0) {
      return;
    }

    initialSentRef.current = true;
    sendMapSearchTelemetry();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [features, sendMapSearchTelemetry]);

  return null;
};
